import React, { useState, useEffect, useMemo, useRef } from 'react';
import useFleetGoogleMaps from '../utils/useFleetGoogleMaps';
import './DeviceMonitor.css';
import { toast } from 'react-toastify';

export default function DeviceMonitor({ devices = [], user = '' }) {
  const [hasAccess, setHasAccess] = useState(null); // null = loading, true/false = result
  const [requestSent, setRequestSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deviceList, setDeviceList] = useState([]);
  const [fetchingDevices, setFetchingDevices] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceDetails, setDeviceDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [commandText, setCommandText] = useState('');
  const [commandSending, setCommandSending] = useState(false);
  const [commandLog, setCommandLog] = useState([]);
  const commandLogRef = useRef(null);

  const userEmail = useMemo(() => {
    return user || localStorage.getItem('authUser') || '';
  }, [user]);

  const apiKey = useMemo(() => {
    const fromEnv = process.env.REACT_APP_API_KEY;
    if (fromEnv) return fromEnv;
    if (typeof window !== 'undefined' && window.REACT_APP_API_KEY) {
      return window.REACT_APP_API_KEY;
    }
    return '';
  }, []);

  const { googleMapsApiKey: googleKey } = useFleetGoogleMaps();

  const accessStatus = useMemo(() => {
    if (hasAccess === null) {
      return { label: 'Checking', type: 'checking' };
    }
    if (hasAccess === true) {
      return { label: 'Active', type: 'active' };
    }
    if (requestSent) {
      return { label: 'Pending approval', type: 'pending' };
    }
    return { label: 'No access', type: 'none' };
  }, [hasAccess, requestSent]);

  const baseUrl = process.env.REACT_APP_DEVICEMONITOR_API_BASE ;

  const statusApiUrl = process.env.REACT_APP_TRACKCURRENT_API_BASE || `${process.env.REACT_APP_TRACK_API_BASE}/testing3`;
  const offlineThresholdSeconds = 300;

  const parseTimestamp = ts => {
    if (!ts) return null;
    const s = String(ts);
    if (s.length < 14) return null;
    const y = s.slice(0, 4);
    const m = s.slice(4, 6);
    const d = s.slice(6, 8);
    const hh = s.slice(8, 10);
    const mm = s.slice(10, 12);
    const ss = s.slice(12, 14);
    const iso = `${y}-${m}-${d}T${hh}:${mm}:${ss}Z`;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  };

  const formatDisplayTime = date => {
    if (!date) return '';
    try {
      return date.toLocaleString();
    } catch {
      return '';
    }
  };

  const getAddressForCoords = async (lat, lng) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return '';
    }
    if (!googleKey) {
      return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    }
    try {
     const res = await fetch(
         `${process.env.REACT_APP_GOOGLE_GEOCODE_API_URL || 'https://maps.googleapis.com/maps/api/geocode/json'}?latlng=${lat},${lng}&key=${googleKey}`
      );

      const json = await res.json();
      if (json && Array.isArray(json.results) && json.results[0]) {
        return json.results[0].formatted_address;
      }
    } catch (e) {
      console.error('Geocoding error', e);
    }
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  };

  const copyToClipboard = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Unable to copy ${label}`);
    }
  };

  const fetchLatestStatus = async imei => {
    try {
      const url = `${statusApiUrl}?imeis=${encodeURIComponent(imei)}`;
      const res = await fetch(url, {
        headers: apiKey ? { 'x-api-key': apiKey } : undefined
      });
      if (res.status === 404) {
        return null;
      }
      if (!res.ok) {
        return null;
      }
      let json = {};
      try {
        json = await res.json();
      } catch {
        json = {};
      }
      let payload = json;
      if (typeof payload?.body === 'string') {
        try {
          payload = JSON.parse(payload.body);
        } catch {
          payload = json;
        }
      }
      const arr = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      if (!arr.length) {
        return null;
      }
      const last =
        arr.reduce(
          (a, b) => (a && a.time_stamp > b.time_stamp ? a : b),
          null
        ) || arr[arr.length - 1];
      if (!last) {
        return null;
      }
      const stamp = String(last.time_stamp || '');
      const lastDate = parseTimestamp(stamp);
      const nowDate = new Date();
      let status = 'Offline';
      if (lastDate) {
        const diffSeconds = (nowDate - lastDate) / 1000;
        if (diffSeconds <= offlineThresholdSeconds) {
          const sp = Number(last.speed);
          if (Number.isFinite(sp) && sp > 3) {
            status = 'Running';
          } else {
            status = 'Idle';
          }
        }
      }
      const lat = Number(last.latitude);
      const lng = Number(last.longitude);
      let address = '';
      let latValue = null;
      let lngValue = null;
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        latValue = lat;
        lngValue = lng;
        address = await getAddressForCoords(lat, lng);
      }
      return {
        imei,
        status,
        lastPacketStamp: stamp,
        lastPacketDate: lastDate,
        lat: latValue,
        lng: lngValue,
        address
      };
    } catch (e) {
      console.error('[DeviceMonitor] Failed to fetch latest device details', e);
      return null;
    }
  };

  // Check user access when component loads
  useEffect(() => {
    if (!userEmail) {
      setHasAccess(false);
      return;
    }
    checkAccessStatus();
  }, [userEmail]);

  // Load devices after access is verified
  useEffect(() => {
    if (hasAccess === true && userEmail) {
      fetchUserDevices();
    }
  }, [hasAccess, userEmail]);

  useEffect(() => {
    let cancelled = false;
    let intervalId;

    const loadDetails = async () => {
      if (!selectedDevice) {
        return;
      }
      setDetailsLoading(true);
      const details = await fetchLatestStatus(selectedDevice.imei);
      if (!cancelled) {
        setDeviceDetails(details);
        setDetailsLoading(false);
      }
    };

    if (selectedDevice) {
      loadDetails();
      intervalId = setInterval(loadDetails, 15000);
    } else {
      setDeviceDetails(null);
    }

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [selectedDevice]);

  async function fetchUserDevices() {
    if (!userEmail) return;
    if (fetchingDevices) return;
    setFetchingDevices(true);
    try {
      const usernameNow = localStorage.getItem('authUser') || userEmail;
      const ts = Date.now();
       const url = `${process.env.REACT_APP_POST_DEVICE_BASE }?TableName=TabUserDetails&username=${encodeURIComponent(

        usernameNow
      )}&_=${ts}`;
      const res = await fetch(url, {
        headers: { 'x-api-key': apiKey },
        cache: 'reload'
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      const list = (json && json.data && json.data.devices) || [];
      const normalized = Array.isArray(list)
        ? list
            .map(d => ({
              name: d.deviceName || d.name || 'Device',
              imei: d.imei || '',
              deviceType: d.deviceType || 'Unknown'
            }))
            .filter(d => d.imei)
        : [];
      setDeviceList(normalized);
      if (normalized.length === 0) {
        toast.info('No devices registered for this account');
      }
    } catch (error) {
      console.error('[DeviceMonitor] Failed to fetch devices:', error);
      toast.error('Failed to load devices for Device Monitor');
      setDeviceList([]);
    } finally {
      setFetchingDevices(false);
    }
  }

  // Use devices from props when available
  useEffect(() => {
    if (hasAccess === true && devices && Array.isArray(devices) && devices.length > 0) {
      const normalized = devices
        .map(d => ({
          name: d.name || d.deviceName || 'Device',
          imei: d.imei || '',
          deviceType: d.deviceType || 'Unknown'
        }))
        .filter(d => d.imei);
      if (normalized.length > 0) {
        setDeviceList(normalized);
      }
    }
  }, [hasAccess, devices]);

  const handleSelectDevice = device => {
    setSelectedDevice(device);
    setCommandLog([]);
  };

  // Check if user has Device Monitor access
  
  const checkAccessStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/check-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        },
        body: JSON.stringify({
          action: 'CHECK_ACCESS',
          userEmail: userEmail
        })
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      const data = await res.json();
      const access =
        data.deviceMonitor === true ||
        data.hasAccess === true ||
        data.access === true;
      setHasAccess(access);
      
      if (!access) {
        console.log('[DeviceMonitor] Access denied for:', userEmail);
      }
    } catch (error) {
      console.error('[DeviceMonitor] Error checking access:', error.message);
      toast.error('Unable to check access. Backend may not be configured yet.');
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  //  Request Device Monitor access from admin
   
  const handleRequestAccess = async () => {
    if (!userEmail || requestSent || loading) return;

    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/request-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey
        },
        body: JSON.stringify({
          action: 'REQUEST_ACCESS',
          userEmail: userEmail
        })
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setRequestSent(true);
      toast.success('Request sent to admin. You will get access once approved.');
    } catch (error) {
      console.error('[DeviceMonitor] Error requesting access:', error.message);
      toast.error('Unable to send request. Backend may not be configured yet.');
    } finally {
      setLoading(false);
    }
  };

  // Refresh access status
   
  const handleRefreshStatus = async () => {
    setRequestSent(false);
    await checkAccessStatus();
  };

  const handleSendCommand = async () => {
    if (!selectedDevice) {
      toast.warn('Select a device first');
      return;
    }
    const trimmed = commandText.trim();
    if (!trimmed) {
      toast.warn('Enter a command');
      return;
    }
    if (commandSending) {
      return;
    }

    const imei = selectedDevice.imei;
    const timestamp = new Date().toISOString();
    const outgoingEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      imei,
      direction: 'out',
      status: 'command',
      text: trimmed,
      ts: timestamp
    };
    setCommandLog(prev => [...prev, outgoingEntry]);
    setCommandText('');
    setCommandSending(true);

    try {
      const res = await fetch('http://13.250.175.209:8007/command', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imei,
          command: trimmed
        })
      });
      const text = await res.text();
      const now = new Date().toISOString();
      let parsed;
      try {
        parsed = text ? JSON.parse(text) : null;
      } catch {
        parsed = null;
      }
      const baseMessage =
        parsed && typeof parsed === 'object'
          ? JSON.stringify(parsed)
          : text || 'No response body';
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        imei,
        direction: 'in',
        status: res.ok ? 'success' : res.status === 405 ? 'no-response' : 'error',
        text: baseMessage,
        ts: now
      };
      setCommandLog(prev => [...prev, entry]);
      if (res.ok) {
        toast.success('Command sent successfully');
      } else if (res.status === 405) {
        toast.error('Command API method not allowed (405)');
      } else {
        toast.error(`Command failed with status ${res.status}`);
      }
    } catch (error) {
      console.error('[DeviceMonitor] Error sending command:', error);
      const now = new Date().toISOString();
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        imei: selectedDevice.imei,
        direction: 'in',
        status: 'error',
        text: 'Network error or timeout while sending command',
        ts: now
      };
      setCommandLog(prev => [...prev, entry]);
      toast.error('Failed to send command');
    } finally {
      setCommandSending(false);
    }
  };

  const filteredLog = useMemo(() => {
    if (!selectedDevice) {
      return [];
    }
    return commandLog.filter(entry => entry.imei === selectedDevice.imei);
  }, [commandLog, selectedDevice]);

  useEffect(() => {
    if (!commandLogRef.current) {
      return;
    }
    commandLogRef.current.scrollTop = commandLogRef.current.scrollHeight;
  }, [filteredLog]);

  const handleClearCommandLog = () => {
    if (!selectedDevice) {
      return;
    }
    const imei = selectedDevice.imei;
    setCommandLog(prev => prev.filter(entry => entry.imei !== imei));
  };

  // Render Loading state
  if (hasAccess === null) {
    return (
      <div className="device-monitor">
        <div className="dm-loading-container">
          <div className="dm-spinner" />
          <p>Checking access...</p>
        </div>
      </div>
    );
  }

  // Render Restricted Access state
  if (!hasAccess) {
    return (
      <div className="device-monitor">
        <div className="dm-restricted-container">
          <div className="dm-restricted-card">
            <div className="dm-status-row">
              <div className={`dm-status-pill dm-status-${accessStatus.type}`}>
                <span className="dm-status-dot" />
                <span>{accessStatus.label}</span>
              </div>
            </div>
            <div className="dm-lock-icon">🔒</div>
            <p className="dm-restricted-description">
              You need admin approval to access device monitoring features.
            </p>

            <div className="dm-action-group">
              {!requestSent ? (
                <button
                  className="dm-request-btn"
                  onClick={handleRequestAccess}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="dm-btn-spinner" />
                      Sending...
                    </>
                  ) : (
                    <>
                      📨 Request Access
                    </>
                  )}
                </button>
              ) : (
                <div className="dm-success-message">
                  <div>
                    <p>Request sent to admin!</p>
                    <p style={{ fontSize: '13px', marginTop: '4px' }}>
                      You'll get access once approved.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <button
              className="dm-refresh-bottom-btn"
              onClick={handleRefreshStatus}
              disabled={loading}
            >
              Check Status
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="device-monitor">
      <div className="dm-header">
        <div className="dm-header-content">
          <p className="dm-subtitle">Total IMEIs: {deviceList.length}</p>
        </div>
        <div className="dm-header-status">
          <div className="dm-status-pill dm-status-active">
            <span className="dm-status-dot" />
            <span>Active</span>
          </div>
        </div>
      </div>

      {deviceList.length === 0 ? (
        <div className="dm-empty-state">
          <div className="dm-empty-icon">📭</div>
          <h2>No Devices</h2>
          <p>You don't have any devices registered yet.</p>
        </div>
      ) : (
        <div className="dm-layout">
          <div className="dm-left-panel">
            <div className="dm-left-header">
              {fetchingDevices && (
                <div className="dm-left-loading">Loading…</div>
              )}
            </div>
            <div className="dm-left-list">
              {deviceList.map(device => (
                <button
                  key={device.imei}
                  className={
                    selectedDevice && selectedDevice.imei === device.imei
                      ? 'dm-left-item dm-left-item-active'
                      : 'dm-left-item'
                  }
                  onClick={() => handleSelectDevice(device)}
                >
                  <div className="dm-left-main">
                    <div className="dm-left-name">{device.name}</div>
                    <div className="dm-left-imei mono">{device.imei}</div>
                  </div>
                  <div className="dm-left-select">Select</div>
                </button>
              ))}
            </div>
            <div className="dm-left-footer">
              <p className="dm-left-hint">Select a device to view details</p>
            </div>
          </div>

          <div
            className={
              selectedDevice ? 'dm-right-panel dm-right-panel-active' : 'dm-right-panel'
            }
          >
            {!selectedDevice ? (
              <div className="dm-right-empty">
                <p>Select a device to view details</p>
              </div>
            ) : (
              <>
                <div className="dm-right-section">
                  <div className="dm-section-header">
                    <h2 className="dm-section-title">Device Details</h2>
                    <button
                      type="button"
                      className="dm-copy-btn"
                      onClick={() =>
                        copyToClipboard(
                          selectedDevice.imei,
                          'IMEI'
                        )
                      }
                    >
                      Copy IMEI
                    </button>
                  </div>
                  <div className="dm-detail-grid">
                    <div className="dm-detail-row">
                      <span className="dm-detail-label">IMEI</span>
                      <span className="dm-detail-value mono">
                        {selectedDevice.imei}
                      </span>
                    </div>
                    <div className="dm-detail-row">
                      <span className="dm-detail-label">Status</span>
                      <span
                        className={
                          deviceDetails
                            ? deviceDetails.status === 'Running'
                              ? 'dm-device-status dm-device-status-running'
                              : deviceDetails.status === 'Idle'
                              ? 'dm-device-status dm-device-status-idle'
                              : 'dm-device-status dm-device-status-offline'
                            : 'dm-device-status dm-device-status-loading'
                        }
                      >
                        {detailsLoading && !deviceDetails
                          ? 'Loading...'
                          : deviceDetails
                          ? deviceDetails.status
                          : 'Unknown'}
                      </span>
                    </div>
                    <div className="dm-detail-row">
                      <span className="dm-detail-label">Last Packet Time</span>
                      <span className="dm-detail-value">
                        {deviceDetails && deviceDetails.lastPacketDate
                          ? formatDisplayTime(deviceDetails.lastPacketDate)
                          : '-'}
                      </span>
                    </div>
                    <div className="dm-detail-row">
                      <span className="dm-detail-label">Latitude &amp; Longitude</span>
                      <span className="dm-detail-value">
                        {deviceDetails &&
                        typeof deviceDetails.lat === 'number' &&
                        typeof deviceDetails.lng === 'number'
                          ? `${deviceDetails.lat.toFixed(
                              5
                            )}, ${deviceDetails.lng.toFixed(5)}`
                          : '-'}
                      </span>
                    </div>
                    <div className="dm-detail-row">
                      <span className="dm-detail-label">Address</span>
                      <span className="dm-detail-value">
                        {deviceDetails && deviceDetails.address
                          ? deviceDetails.address
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="dm-right-section">
                  <div className="dm-section-header">
                    <h2 className="dm-section-title">Command</h2>
                    <button
                      type="button"
                      className="dm-copy-btn"
                      onClick={() => {
                        if (
                          deviceDetails &&
                          typeof deviceDetails.lat === 'number' &&
                          typeof deviceDetails.lng === 'number'
                        ) {
                          const value = `${deviceDetails.lat},${deviceDetails.lng}`;
                          copyToClipboard(value, 'Location');
                        }
                      }}
                    >
                      Copy Location
                    </button>
                    <button
                      type="button"
                      className="dm-copy-btn"
                      onClick={() => {
                        if (deviceDetails && deviceDetails.address) {
                          copyToClipboard(deviceDetails.address, 'Address');
                        }
                      }}
                    >
                      Copy Address
                    </button>
                  </div>
                  <div className="dm-command-row">
                    <input
                      className="dm-command-input"
                      type="text"
                      placeholder="Enter command"
                      value={commandText}
                      onChange={e => setCommandText(e.target.value)}
                      disabled={!selectedDevice || commandSending}
                    />
                    <button
                      type="button"
                      className="dm-command-send-btn"
                      onClick={handleSendCommand}
                      disabled={
                        !selectedDevice ||
                        !commandText.trim() ||
                        commandSending
                      }
                    >
                      {commandSending ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>

                <div className="dm-right-section">
                  <div className="dm-section-header">
                    <h2 className="dm-section-title">Command Response</h2>
                    <button
                      type="button"
                      className="dm-copy-btn"
                      onClick={handleClearCommandLog}
                      disabled={filteredLog.length === 0}
                    >
                      Clear Chat
                    </button>
                  </div>
                  <div className="dm-command-log" ref={commandLogRef}>
                    {filteredLog.length === 0 ? (
                      <div className="dm-log-empty">
                        <p>No commands sent yet for this device.</p>
                      </div>
                    ) : (
                      filteredLog.map(entry => (
                        <div
                          key={entry.id}
                          className={`dm-log-entry ${
                            entry.direction === 'out'
                              ? 'dm-log-outgoing'
                              : 'dm-log-incoming'
                          } dm-log-${entry.status}`}
                        >
                          <div className="dm-log-meta">
                            <span className="dm-log-author">
                              {entry.direction === 'out' ? 'You' : 'Device'}
                            </span>
                            <span className="dm-log-time">
                              {formatDisplayTime(new Date(entry.ts))}
                            </span>
                          </div>
                          <div className="dm-log-bubble">
                            {entry.text}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
